import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Switch } from '@mui/material';
import { useFilterContext } from './filtercontext';

// "Withheld flags" — the contextual-flag-assessment §4.8 review control.
//
// ON narrows the table to rows whose AI flag the case flag assessment WITHHELD
// (the :SUPPRESSED edge). Those rows are not in the default feed at all — the
// enriched read only adds them when asked (include_withheld) — so this filter
// does double duty a plain SwitchFilter cannot: it registers the client-side
// narrowing AND tells the page owner to re-fetch the feed with the server-side
// opt-in. The page wires its setter in as `onIncludeChange`.
//
// Stability (filter-component-stability rules 1/2): the callback is read
// through a ref, so an unstable setter identity can never re-fire the register
// effect; the effect's deps are the switch value plus the two constant strings.
// The notify is a boolean straight into a setState — React bails out on the
// same value, so calling it with the current state is content-gated by
// construction. It is also called on mount: a remount (DataFilter re-key)
// resets the switch, and the fetch opt-in must reset with it — the two halves
// of this control can never disagree.
//
// OFF (default) is the app exactly as before: live flags only, no opt-in.
// With the stage dark (no assessments exist) the opt-in simply returns no
// SUPPRESSED rows and the table shows its normal empty result — absence never
// looks broken.
export default function WithheldFlagsFilter({
  field = 'isWithheldFlag',
  label = 'Withheld flags',
  onIncludeChange = null,
}) {
  const { registerFilter, clearFilter } = useFilterContext();

  const [onlyWithheld, setOnlyWithheld] = useState(false);

  const onIncludeChangeRef = useRef(onIncludeChange);
  useEffect(() => {
    onIncludeChangeRef.current = onIncludeChange;
  }, [onIncludeChange]);

  const handleClearFromContext = () => {
    setOnlyWithheld(false);
  };

  useEffect(() => {
    const notify = (included) => {
      const cb = onIncludeChangeRef.current;
      if (cb) cb(included);
    };

    if (onlyWithheld) {
      // Opt the feed in FIRST — the switch turning on must never show an empty
      // table while the include_withheld read is still in flight.
      notify(true);
      registerFilter(
        field,
        label,
        (item) => item[field] === true,
        handleClearFromContext,
        () => `${label}: Only`,
      );
    } else {
      notify(false);
      clearFilter(field);
    }
    // registerFilter/clearFilter are deliberately NOT deps (stable in the
    // context by contract; rule 2) and onIncludeChange is read via its ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyWithheld, field, label]);

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>

      <Switch
        checked={onlyWithheld}
        onChange={(e) => setOnlyWithheld(e.target.checked)}
        color="primary"
        inputProps={{ 'aria-label': label }}
      />

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        Show only flags the case assessment withheld. Restoring one makes it a human flag, final.
      </Typography>
    </Box>
  );
}
